import React, { useState, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useBreakpoint, useBreakpointDown } from '@/hooks/useBreakpoint';
import { apiClient } from '@/services/api';
import { usePageConfig } from '@/contexts/PageConfigContext';
import { LeaderboardPreview } from '@/components/LeaderboardPreview';
import { TournamentInfoCard } from '@/components/TournamentInfoCard';
import { UserProgressCard } from '@/components/UserProgressCard';
import Error404 from '@/components/Error404';
import { PillButton } from '@/components/ui/PillButton';
import { Popup } from '@/components/Popup';
import EnhancedTournamentInviteForm from '@/components/EnhancedTournamentInviteForm';
import type { TournamentParticipant, StatusFilter, RoleFilter, SortField, SortOrder } from '@/components/ParticipantsTable';
import { Card } from '@/components/layout';
import { ContentLayout } from '@/layouts/ContentLayout';
import { setCachedTournamentRole, getCachedTournamentRole, isPrivilegedTournamentRole } from '@/utils/tournamentRoleCache';
import { PARTICIPANT_STATUSES } from '@/constants';
import { TournamentDetailContext } from './context';
import type { TournamentDetailContextType, Tournament, LeaderboardData } from './context';
import MatchesTab from '@/pages/tournament-detail/MatchesTab';
import ParticipantsTab from '@/pages/tournament-detail/ParticipantsTab';
import LeaderboardTab from '@/pages/tournament-detail/LeaderboardTab';
import SettingsTab from '@/pages/tournament-detail/SettingsTab';
import styles from '@/pages/TournamentDetailPage.module.scss';
import participantsStyles from '@/pages/TournamentParticipantsPage.module.scss';

// === useParticipantActions hook ===

interface UseParticipantActionsParams {
  tournamentId: string;
  refetchParticipants: () => void;
}

function useParticipantActions({ tournamentId, refetchParticipants }: UseParticipantActionsParams) {
  const { t } = useTranslation(['tournament', 'common']);

  const [blockModalParticipant, setBlockModalParticipant] = useState<TournamentParticipant | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    participant: TournamentParticipant;
    type: 'delete' | 'revoke';
  } | null>(null);

  // Filter/sort/pagination state
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [sortBy, setSortBy] = useState<SortField>('last_activity_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [participantsPage, setParticipantsPage] = useState(0);
  const [participantsRowsPerPage, setParticipantsRowsPerPage] = useState(10);

  const handleRoleChange = useCallback(async (participant: TournamentParticipant, newRole: 'participant' | 'maintainer') => {
    if (participant.role === newRole) return;
    try {
      setActionLoading(participant.id);
      await apiClient.updateParticipantRole(parseInt(tournamentId), participant.user_id, newRole);
      toast.success(t('tournament:roleChangedTo', { role: newRole }));
      await refetchParticipants();
    } catch (err: any) {
      console.error('Error changing participant role:', err);
      toast.error(err.response?.data?.message || t('tournament:errorChangingRole'));
    } finally {
      setActionLoading(null);
    }
  }, [tournamentId, refetchParticipants, t]);

  const handleBlockToggle = useCallback(async (participant: TournamentParticipant, reason: string) => {
    try {
      setActionLoading(participant.id);
      const shouldBlock = participant.status !== PARTICIPANT_STATUSES.BLOCKED;
      await apiClient.toggleTournamentParticipantBlock(
        parseInt(tournamentId),
        participant.user_id,
        { block: shouldBlock, reason },
      );
      await refetchParticipants();
    } catch (err: any) {
      console.error('Error toggling participant block:', err);
      toast.error(err.response?.data?.message || t('tournament:errorChangingStatus'));
    } finally {
      setActionLoading(null);
    }
  }, [tournamentId, refetchParticipants, t]);

  const handleRevokeInvite = useCallback((participant: TournamentParticipant) => {
    if (!participant.invite_code) {
      toast.error(t('tournament:inviteCodeNotFound'));
      return;
    }
    setConfirmAction({ participant, type: 'revoke' });
  }, [t]);

  const handleDeleteParticipant = useCallback((participant: TournamentParticipant) => {
    setConfirmAction({ participant, type: 'delete' });
  }, []);

  const handleConfirmAction = useCallback(async () => {
    if (!confirmAction) return;
    const { participant, type } = confirmAction;

    try {
      setActionLoading(participant.id);
      if (type === 'revoke') {
        await apiClient.revokeInvitation(participant.invite_code!);
        toast.success(t('tournament:invitationRevoked'));
      } else {
        await apiClient.deleteTournamentParticipant(parseInt(tournamentId), participant.user_id);
        toast.success(t('tournament:participantRemoved'));
      }
      await refetchParticipants();
    } catch (err: any) {
      console.error(`Error ${type} participant:`, err);
      const msg = type === 'revoke'
        ? err.response?.data?.error || t('tournament:errorRevokingInvitation')
        : err.response?.data?.message || t('tournament:errorRemovingParticipant');
      toast.error(msg);
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  }, [confirmAction, tournamentId, refetchParticipants, t]);

  const handleSortChange = useCallback((field: SortField, order: SortOrder) => {
    setSortBy(field);
    setSortOrder(order);
    setParticipantsPage(0);
  }, []);

  const handleStatusFilterChange = useCallback((status: StatusFilter) => {
    setStatusFilter(status);
    setParticipantsPage(0);
  }, []);

  const handleRoleFilterChange = useCallback((role: RoleFilter) => {
    setRoleFilter(role);
    setParticipantsPage(0);
  }, []);

  return {
    blockModalParticipant,
    setBlockModalParticipant,
    actionLoading,
    showInviteModal,
    setShowInviteModal,
    confirmAction,
    setConfirmAction,
    statusFilter,
    roleFilter,
    sortBy,
    sortOrder,
    participantsPage,
    participantsRowsPerPage,
    setParticipantsPage,
    setParticipantsRowsPerPage,
    handleRoleChange,
    handleBlockToggle,
    handleRevokeInvite,
    handleDeleteParticipant,
    handleConfirmAction,
    handleSortChange,
    handleStatusFilterChange,
    handleRoleFilterChange,
  };
}

// === BlockModal component ===

interface BlockModalProps {
  participant: TournamentParticipant;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

const BlockModal: React.FC<BlockModalProps> = ({ participant, isOpen, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const { t } = useTranslation(['tournament', 'common']);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reason.trim() || participant.status === PARTICIPANT_STATUSES.BLOCKED) {
      onConfirm(reason.trim());
      setReason('');
      onClose();
    }
  };

  const isBlocked = participant.status === PARTICIPANT_STATUSES.BLOCKED;

  return (
    <div className={participantsStyles.modalOverlay}>
      <div className={participantsStyles.modalCard}>
        <h3 className={participantsStyles.modalTitle}>
          {isBlocked ? t('tournament:unblockParticipant') : t('tournament:blockParticipant')}
        </h3>

        <p className={participantsStyles.modalParticipantInfo}>
          {t('tournament:participantLabel', { name: '' })}<strong>{participant.first_name} {participant.last_name}</strong>
          {participant.username && ` (@${participant.username})`}
        </p>

        {isBlocked && participant.block_reason && (
          <div className={participantsStyles.modalCurrentReason}>
            <strong>{t('tournament:currentReason')}</strong> {participant.block_reason}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {!isBlocked && (
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className={participantsStyles.modalTextarea}
              placeholder={t('tournament:blockReason')}
              required
            />
          )}

          <div className={participantsStyles.modalActions}>
            <button
              type="button"
              onClick={onClose}
              className={`${participantsStyles.modalBtn} ${participantsStyles.cancel}`}
            >
              {t('common:cancel')}
            </button>
            <button
              type="submit"
              className={`${participantsStyles.modalBtn} ${participantsStyles.confirm} ${isBlocked ? participantsStyles.confirmUnblock : participantsStyles.confirmBlock}`}
            >
              {isBlocked ? t('tournament:unblock') : t('tournament:block')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// === ConfirmModal component ===

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  participantName: string;
  confirmLabel: string;
  confirmClassName?: string;
  onClose: () => void;
  onConfirm: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  participantName,
  confirmLabel,
  confirmClassName,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation('common');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm();
    onClose();
  };

  return (
    <div className={participantsStyles.modalOverlay}>
      <div className={participantsStyles.modalCard}>
        <h3 className={participantsStyles.modalTitle}>{title}</h3>
        <p className={participantsStyles.modalParticipantInfo}>
          {participantName}
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '8px 0 16px' }}>
          {message}
        </p>
        <form onSubmit={handleSubmit}>
          <div className={participantsStyles.modalActions}>
            <button
              type="button"
              onClick={onClose}
              className={`${participantsStyles.modalBtn} ${participantsStyles.cancel}`}
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              className={`${participantsStyles.modalBtn} ${participantsStyles.confirm} ${confirmClassName || participantsStyles.confirmBlock}`}
            >
              {confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// === BlockedState component ===

interface BlockedStateProps {
  blockReason?: string | null;
}

const BlockedState: React.FC<BlockedStateProps> = ({ blockReason }) => {
  const { t } = useTranslation(['tournament']);

  return (
    <div className="ls-flex ls-align-center ls-justify-center" style={{ minHeight: '60vh' }}>
      <Card className="ls-text-center" style={{ maxWidth: '400px', width: '100%' }}>
        <div className="ls-mb-4">
          <div
            className="ls-flex ls-align-center ls-justify-center"
            style={{
              width: '80px',
              height: '80px',
              background: 'var(--bg-error)',
              borderRadius: '50%',
              margin: '0 auto',
            }}
          >
            <svg className="theme-text-error" width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
        </div>

        <h2 className="ls-h2 ls-mb-2">{t('tournament:youAreBlocked')}</h2>
        <p className="theme-text-secondary ls-mb-4" style={{ fontSize: '13px' }}>
          {t('tournament:blockedInTournament')}
        </p>

        {blockReason && (
          <div className="theme-bg-error ls-py-3 ls-px-3 ls-mb-4" style={{ borderRadius: '8px', borderLeft: '4px solid var(--text-error)' }}>
            <div className="ls-flex ls-align-start ls-gap-2">
              <svg className="theme-text-error" width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="ls-text-left">
                <p className="ls-label theme-text-error ls-mb-1">{t('tournament:reason')}</p>
                <p className="theme-text-error" style={{ fontSize: '12px' }}>{blockReason}</p>
              </div>
            </div>
          </div>
        )}

        <Link to="/tournaments" className="ls-btn" style={{ width: '100%' }}>
          {t('tournament:backToTournaments')}
        </Link>
      </Card>
    </div>
  );
};

// === Main TournamentDetailLayout component ===

const TournamentDetailLayout: React.FC = () => {
  const { id, tournamentId: paramTournamentId } = useParams<{ id?: string; tournamentId?: string }>();
  const tournamentId = id || paramTournamentId;
  const { isAuthenticated, user } = useAuth();
  const { isDesktop } = useBreakpoint();
  const isMobile = useBreakpointDown('lg');
  const { t } = useTranslation(['tournament', 'common']);
  const { setConfig } = usePageConfig();

  // === DATA FETCHING ===

  const {
    data: tournament,
    isLoading: tournamentLoading,
    error: tournamentError,
    refetch: refetchTournament,
  } = useQuery<Tournament>({
    queryKey: ['tournament', tournamentId],
    queryFn: async () => {
      const response = await apiClient.getTournament(tournamentId!);
      return response.data?.data || response.data || response;
    },
    enabled: !!tournamentId && isAuthenticated,
  });

  const { data: leaderboardData } = useQuery<LeaderboardData>({
    queryKey: ['tournament-leaderboard', tournamentId],
    queryFn: async () => {
      const response = await apiClient.getTournamentLeaderboard(tournamentId!, 100);
      return response.data?.data || response.data || response;
    },
    enabled: !!tournamentId && isAuthenticated,
  });

  // Participant actions hook (state + handlers for participants tab)
  const participantActions = useParticipantActions({
    tournamentId: tournamentId!,
    refetchParticipants: () => refetchParticipantsQuery(),
  });

  const {
    statusFilter, roleFilter, sortBy, sortOrder,
    participantsPage, participantsRowsPerPage,
    showInviteModal, setShowInviteModal,
    blockModalParticipant, setBlockModalParticipant,
    confirmAction, setConfirmAction,
    handleBlockToggle, handleConfirmAction,
  } = participantActions;

  // Fetch participants — shares cache with ParticipantsTab via the same queryKey.
  // refetchParticipantsQuery is wired into participantActions so block/delete/role-change
  // actions in this layout invalidate the data shown in the tab.
  const {
    refetch: refetchParticipantsQuery,
  } = useQuery({
    queryKey: ['tournament-participants', tournamentId, statusFilter, roleFilter, sortBy, sortOrder, participantsPage, participantsRowsPerPage],
    queryFn: async () => {
      try {
        const [participantsResponse, statsResponse] = await Promise.all([
          apiClient.getTournamentParticipantsManagement(
            parseInt(tournamentId!),
            {
              status: statusFilter === 'all' ? undefined : statusFilter,
              role: roleFilter === 'all' ? undefined : roleFilter,
              sortBy,
              sortOrder,
              page: participantsPage + 1,
              limit: participantsRowsPerPage,
            },
          ),
          apiClient.getTournamentParticipantsStats(parseInt(tournamentId!)),
        ]);

        const apiData = participantsResponse.data?.data || participantsResponse.data;
        const participants = apiData?.participants || apiData;
        const pagination = apiData?.pagination;
        const statsData = statsResponse.data?.data || statsResponse.data;
        const stats = statsData?.stats || statsData;

        return {
          participants: Array.isArray(participants) ? participants as TournamentParticipant[] : [],
          stats: stats as import('./context').ParticipantStats | null,
          totalCount: pagination?.total || (Array.isArray(participants) ? participants.length : 0),
        };
      } catch (err: any) {
        if (err.response?.status === 403) {
          // Soft-fail for non-privileged users so the shared cache is still populated
          // (ParticipantsTab uses the same queryKey). Permission gating is done via
          // tournament.userRole, not via this 403.
          return { participants: [], stats: null, totalCount: 0 };
        }
        throw err;
      }
    },
    enabled: !!tournamentId && isAuthenticated,
  });

  const leaderboard = leaderboardData?.leaderboard || [];
  const userPosition = leaderboardData?.user_position;

  // Permissions — derived from tournament.userRole when loaded, falling back to the localStorage
  // role cache while loading. The cache fallback keeps `canManageParticipants` stable across
  // initial render → loading useLayoutEffect → loaded useLayoutEffect (same value all 3 phases),
  // so hiddenNavPaths never changes and the nav swiper does not re-render with a different tab set.
  // Stale-cache caveat: if an admin demoted the user while they were offline, cache shows the old
  // privileged role until the API response lands → brief 4→2 collapse on that one reload. Rare;
  // server-side 403 still protects writes either way.
  const userRole = tournament?.userRole;
  const cachedRole = useMemo(() => (
    tournamentId ? getCachedTournamentRole(tournamentId) : null
  ), [tournamentId]);
  const effectiveRole: string | null = userRole ?? cachedRole;
  const canInvite = isPrivilegedTournamentRole(effectiveRole);
  const canChangeRoles = canInvite;
  const canManageParticipants = canInvite;
  const canAccessSettings = canManageParticipants;
  const isArchived = !!tournament?.archivedAt;

  // hiddenNavPaths follow the same default-deny rule: hide privileged tabs until proven allowed.
  const privilegedHiddenNavPaths = useMemo(() => (
    canManageParticipants
      ? []
      : [
          `/tournaments/${tournamentId}/participants`,
          `/tournaments/${tournamentId}/settings`,
        ]
  ), [canManageParticipants, tournamentId]);

  // Persist role to localStorage so getInitialConfig can resolve nav visibility synchronously
  // on the next cold reload — eliminates the 2→4 tabs expansion for privileged users.
  useEffect(() => {
    if (userRole && tournamentId) {
      setCachedTournamentRole(tournamentId, userRole);
    }
  }, [userRole, tournamentId]);

  // === PAGE CONFIG (useLayoutEffect to avoid burger→back flash) ===
  useLayoutEffect(() => {
    const status = (tournamentError as any)?.response?.status;
    if (tournamentLoading) {
      setConfig({
        title: '',
        headerSolid: true,
        hiddenNavPaths: privilegedHiddenNavPaths,
      });
    } else if (tournamentError || !tournament) {
      if (status === 404 || status === 400) {
        setConfig({ title: t('common:notFound'), showBottomNav: false, showNavigation: false, headerSolid: true });
      } else {
        setConfig({ title: t('common:error'), headerSolid: true });
      }
    } else if (tournament.userStatus === PARTICIPANT_STATUSES.BLOCKED) {
      setConfig({ title: tournament.name, showNavigation: false, headerSolid: true });
    } else {
      setConfig({
        title: tournament.name,
        headerSolid: true,
        hiddenNavPaths: privilegedHiddenNavPaths,
      });
    }
  }, [tournamentLoading, tournamentError, tournament, privilegedHiddenNavPaths, setConfig, t]);

  // === TABS (must be before early returns — Rules of Hooks) ===
  const tabs = useMemo(() => {
    if (!tournament || !tournamentId) return [];
    const result = [
      { component: MatchesTab, path: `/tournaments/${tournamentId}` },
    ];
    if (canManageParticipants) {
      result.push({ component: ParticipantsTab, path: `/tournaments/${tournamentId}/participants` });
    }
    result.push({ component: LeaderboardTab, path: `/tournaments/${tournamentId}/leaderboard` });
    if (canAccessSettings) {
      result.push({ component: SettingsTab, path: `/tournaments/${tournamentId}/settings` });
    }
    return result;
  }, [tournament, tournamentId, canManageParticipants, canAccessSettings]);

  const handleInviteError = useCallback((error: string) => {
    toast.error(error || t('tournament:errorSendingInvitations'));
  }, [t]);

  // === LOADING STATE ===
  if (tournamentLoading) {
    return null;
  }

  // === ERROR STATE ===
  if (tournamentError || !tournament) {
    const status = (tournamentError as any)?.response?.status;
    if (status === 404 || status === 400) {
      return <Error404 />;
    }
    return (
      <div className={styles.error}>
        <div className={styles.errorIcon}>!</div>
        <p className={styles.errorText}>
          {tournamentError ? (tournamentError as any).message : t('tournament:tournamentNotFound')}
        </p>
        <Link to="/tournaments" className="ls-btn">{t('tournament:backToTournaments')}</Link>
      </div>
    );
  }

  // === BLOCKED STATE ===
  if (tournament.userStatus === PARTICIPANT_STATUSES.BLOCKED) {
    return <BlockedState blockReason={tournament.userBlockReason} />;
  }

  // === SIDEBAR ===
  const sidebar = (
    <>
      <TournamentInfoCard
        logoUrl={tournament.leagueLogo || tournament.league?.logo_url}
        logoAlt={tournament.leagueName || tournament.league?.name || 'League'}
        title={tournament.name}
        subtitle={tournament.leagueName || tournament.league?.name}
        subtitleTo={tournament.leagueId ? `/matches/${tournament.leagueId}` : undefined}
        stats={[
          { label: t('tournament:matches'), value: tournament.matchesCount || 0 },
          { label: t('tournament:participants'), value: tournament.participantsCount || 0 },
        ]}
        statLayout="row"
        description={tournament.description}
        showDescription={!!tournament.description}
        collapsibleOnMobile={true}
      />

      {isDesktop && canInvite && (
        <PillButton
          onClick={() => setShowInviteModal(true)}
          label={t('tournament:invite')}
          icon={<span>📧</span>}
          className={participantsStyles.inviteBtn}
          data-testid="tournament-invite-button"
        />
      )}

      {isDesktop && (
        <UserProgressCard
          userName={user?.username || user?.email || 'User'}
          position={userPosition?.position}
          stats={[{ label: 'pts', value: userPosition?.total_points || 0 }]}
          statColumns={2}
          positionChange={userPosition?.position_change}
          collapsibleOnMobile={true}
        />
      )}

      {isDesktop && (
        <LeaderboardPreview
          tournamentId={tournamentId!}
          entries={leaderboard}
          currentUserId={user?.id}
        />
      )}
    </>
  );

  // Mobile overlay: invite form replaces tabs
  const mobileOverlay = showInviteModal ? (
    <EnhancedTournamentInviteForm
      tournamentId={parseInt(tournamentId!)}
      inline
      onInviteSent={() => {
        toast.success(t('tournament:invitationsSent'));
        setShowInviteModal(false);
        refetchParticipantsQuery();
      }}
      onError={handleInviteError}
      onClose={() => setShowInviteModal(false)}
    />
  ) : undefined;

  // Build context
  const contextValue: TournamentDetailContextType = {
    tournament,
    tournamentId: tournamentId!,
    refetchTournament,
    userRole,
    canManageParticipants,
    canAccessSettings,
    canInvite,
    canChangeRoles,
    isArchived,
    isMobile,
    refetchParticipants: refetchParticipantsQuery,
    ...participantActions,
  };

  return (
    <TournamentDetailContext.Provider value={contextValue}>
      <ContentLayout
        sidebar={sidebar}
        tabs={tabs}
        mobileOverlay={mobileOverlay}
      />

      {/* Block Modal */}
      {blockModalParticipant && (
        <BlockModal
          participant={blockModalParticipant}
          isOpen={!!blockModalParticipant}
          onClose={() => setBlockModalParticipant(null)}
          onConfirm={(reason) => handleBlockToggle(blockModalParticipant, reason)}
        />
      )}

      {/* Confirm Action Modal (delete participant / revoke invitation) */}
      {confirmAction && (
        <ConfirmModal
          isOpen={!!confirmAction}
          title={confirmAction.type === 'delete' ? t('tournament:deleteParticipant') : t('tournament:revokeInvitation')}
          participantName={`${confirmAction.participant.first_name} ${confirmAction.participant.last_name || ''}${confirmAction.participant.username ? ` (@${confirmAction.participant.username})` : ''}`}
          message={confirmAction.type === 'delete'
            ? t('tournament:deleteParticipantMessage')
            : t('tournament:revokeInvitationMessage')
          }
          confirmLabel={confirmAction.type === 'delete' ? t('common:delete') : t('tournament:revokeInvitation')}
          confirmClassName={participantsStyles.confirmBlock}
          onClose={() => setConfirmAction(null)}
          onConfirm={handleConfirmAction}
        />
      )}

      {/* Invite Modal - desktop only (mobile uses inline form via mobileOverlay) */}
      {!isMobile && (
        <Popup
          open={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          popupClass={participantsStyles.invitePopup}
        >
          <EnhancedTournamentInviteForm
            tournamentId={parseInt(tournamentId!)}
            onInviteSent={() => {
              toast.success(t('tournament:invitationsSent'));
              setShowInviteModal(false);
              refetchParticipantsQuery();
            }}
            onError={handleInviteError}
            onClose={() => setShowInviteModal(false)}
          />
        </Popup>
      )}
    </TournamentDetailContext.Provider>
  );
};

export default TournamentDetailLayout;
